/**
 * Schema Service - Frontend Schema Management
 * 
 * Provides schema loading, validation, and i18n support
 * Driven by backend schemas for consistency
 */

import apiClient from '../lib/api-client';

// Default locale
const DEFAULT_LOCALE = 'en';

// Cache for loaded schemas
const schemaCache = new Map();
const configCache = new Map();

/**
 * SchemaService - Centralized schema management
 */
class SchemaService {
    constructor() {
        this.locale = DEFAULT_LOCALE;
        this.globalSchema = null;
    }

    /**
     * Set current locale for i18n
     * @param {string} locale - Locale code (en, zh-TW, zh-CN, etc.)
     */
    setLocale(locale) {
        this.locale = locale || DEFAULT_LOCALE;
    }

    /**
     * Get current locale
     */
    getLocale() {
        return this.locale;
    }

    /**
     * Load global schema definitions
     */
    async loadGlobalSchema() {
        if (this.globalSchema) return this.globalSchema;

        try {
            const response = await apiClient.get('/schemas/global');
            this.globalSchema = response.data;
            return this.globalSchema;
        } catch (error) {
            console.error('Failed to load global schema:', error);
            // Return default schema structure if API fails
            return this.getDefaultGlobalSchema();
        }
    }

    /**
     * Load form configuration by formId
     * @param {string} formId - Form identifier
     */
    async loadFormConfig(formId) {
        if (!formId) return null;

        const cacheKey = `form:${formId}`;
        if (configCache.has(cacheKey)) {
            return configCache.get(cacheKey);
        }

        try {
            const response = await apiClient.get(`/forms/config/${formId}`);
            const config = response.data;
            configCache.set(cacheKey, config);
            return config;
        } catch (error) {
            console.error(`Failed to load form config for ${formId}:`, error);
            throw error;
        }
    }

    /**
     * Get localized text from i18n object
     * @param {object|string} text - Text object with locale keys or plain string
     * @param {string} locale - Override locale
     * @returns {string} Localized text
     */
    getLocalizedText(text, locale = null) {
        const currentLocale = locale || this.locale;
        
        if (typeof text === 'string') return text;
        if (!text || typeof text !== 'object') return '';

        // Try current locale
        if (text[currentLocale]) return text[currentLocale];
        
        // Try default locale
        if (text[DEFAULT_LOCALE]) return text[DEFAULT_LOCALE];
        
        // Return first available
        const keys = Object.keys(text);
        return keys.length > 0 ? text[keys[0]] : '';
    }

    /**
     * Get field type definition from global schema
     * @param {string} fieldType - Field type name
     */
    getFieldTypeDefinition(fieldType) {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return {
            type: fieldType,
            description: schema.fieldTypes?.descriptions?.[fieldType] || fieldType
        };
    }

    /**
     * Get all available field types
     */
    getFieldTypes() {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return schema.fieldTypes?.enum || [];
    }

    /**
     * Get step type definition
     * @param {string} stepType - Step type name
     */
    getStepTypeDefinition(stepType) {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return {
            type: stepType,
            description: schema.stepTypes?.descriptions?.[stepType] || stepType
        };
    }

    /**
     * Get role type definition
     * @param {string} roleType - Role type name
     */
    getRoleTypeDefinition(roleType) {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return {
            type: roleType,
            description: schema.roleTypes?.descriptions?.[roleType] || roleType
        };
    }

    /**
     * Get action type definition
     * @param {string} actionType - Action type name
     */
    getActionTypeDefinition(actionType) {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return {
            type: actionType,
            description: schema.actionTypes?.descriptions?.[actionType] || actionType
        };
    }

    /**
     * Get currency symbol
     * @param {string} currency - Currency code
     */
    getCurrencySymbol(currency) {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return schema.currencies?.symbols?.[currency] || currency;
    }

    /**
     * Get available currencies
     */
    getCurrencies() {
        const schema = this.globalSchema || this.getDefaultGlobalSchema();
        return schema.currencies?.enum || ['USD', 'CNY', 'TWD'];
    }

    /**
     * Validate field value against schema
     * @param {*} value - Value to validate
     * @param {object} fieldConfig - Field configuration
     * @returns {object} { valid: boolean, errors: string[] }
     */
    validateField(value, fieldConfig) {
        const errors = [];

        // Required check
        if (fieldConfig.required) {
            if (value === undefined || value === null || value === '') {
                errors.push(`${this.getLocalizedText(fieldConfig.label)} is required`);
                return { valid: false, errors };
            }
        }

        // Skip further validation if empty and not required
        if (value === undefined || value === null || value === '') {
            return { valid: true, errors: [] };
        }

        // Type-specific validation
        const validation = fieldConfig.validation || {};

        switch (fieldConfig.type) {
            case 'text':
            case 'textarea':
                if (validation.minLength && value.length < validation.minLength) {
                    errors.push(`Minimum length is ${validation.minLength}`);
                }
                if (validation.maxLength && value.length > validation.maxLength) {
                    errors.push(`Maximum length is ${validation.maxLength}`);
                }
                if (validation.pattern) {
                    const regex = new RegExp(validation.pattern);
                    if (!regex.test(value)) {
                        errors.push(validation.patternMessage || 'Invalid format');
                    }
                }
                break;

            case 'number':
            case 'currency':
                const numValue = parseFloat(value);
                if (isNaN(numValue)) {
                    errors.push('Must be a valid number');
                } else {
                    if (validation.min !== undefined && numValue < validation.min) {
                        errors.push(`Minimum value is ${validation.min}`);
                    }
                    if (validation.max !== undefined && numValue > validation.max) {
                        errors.push(`Maximum value is ${validation.max}`);
                    }
                }
                break;

            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    errors.push('Invalid email format');
                }
                break;

            case 'phone':
                const phoneRegex = /^[\d\-\+\(\)\s]+$/;
                if (!phoneRegex.test(value)) {
                    errors.push('Invalid phone format');
                }
                break;

            case 'url':
                try {
                    new URL(value);
                } catch {
                    errors.push('Invalid URL format');
                }
                break;

            case 'date':
            case 'datetime':
                if (validation.minDate) {
                    const minDate = new Date(validation.minDate);
                    const dateValue = new Date(value);
                    if (dateValue < minDate) {
                        errors.push(`Date must be after ${validation.minDate}`);
                    }
                }
                if (validation.maxDate) {
                    const maxDate = new Date(validation.maxDate);
                    const dateValue = new Date(value);
                    if (dateValue > maxDate) {
                        errors.push(`Date must be before ${validation.maxDate}`);
                    }
                }
                break;

            case 'select':
            case 'radio':
                if (fieldConfig.options) {
                    const validOptions = fieldConfig.options.map(o => o.value);
                    if (!validOptions.includes(value)) {
                        errors.push('Invalid selection');
                    }
                }
                break;

            case 'file':
                if (Array.isArray(value)) {
                    for (const file of value) {
                        if (validation.maxSize && file.size > validation.maxSize) {
                            errors.push(`File size must be less than ${validation.maxSize / 1024 / 1024}MB`);
                        }
                        if (validation.accept && !validation.accept.includes(file.type)) {
                            errors.push(`File type ${file.type} is not allowed`);
                        }
                    }
                }
                break;
        }

        return {
            valid: errors.length === 0,
            errors
        };
    }

    /**
     * Validate entire form
     * @param {object} formData - Form data
     * @param {object} formConfig - Form configuration
     * @returns {object} { valid: boolean, errors: object }
     */
    validateForm(formData, formConfig) {
        const errors = {};
        let isValid = true;

        const fields = formConfig.fields || {};

        for (const [fieldId, fieldConfig] of Object.entries(fields)) {
            const result = this.validateField(formData[fieldId], fieldConfig);
            if (!result.valid) {
                isValid = false;
                errors[fieldId] = result.errors;
            }
        }

        return { valid: isValid, errors };
    }

    /**
     * Evaluate condition
     * @param {object} condition - Condition configuration
     * @param {object} formData - Current form data
     * @returns {boolean} Result of condition evaluation
     */
    evaluateCondition(condition, formData) {
        if (!condition) return true;

        const { field, operator, value } = condition;
        const fieldValue = formData[field];

        switch (operator) {
            case 'equals':
            case '==':
                return fieldValue === value;
            case 'notEquals':
            case '!=':
                return fieldValue !== value;
            case 'greaterThan':
            case '>':
                return parseFloat(fieldValue) > parseFloat(value);
            case 'greaterThanOrEqual':
            case '>=':
                return parseFloat(fieldValue) >= parseFloat(value);
            case 'lessThan':
            case '<':
                return parseFloat(fieldValue) < parseFloat(value);
            case 'lessThanOrEqual':
            case '<=':
                return parseFloat(fieldValue) <= parseFloat(value);
            case 'contains':
                return String(fieldValue).includes(value);
            case 'notContains':
                return !String(fieldValue).includes(value);
            case 'in':
                return Array.isArray(value) && value.includes(fieldValue);
            case 'notIn':
                return Array.isArray(value) && !value.includes(fieldValue);
            case 'isEmpty':
                return !fieldValue || fieldValue === '';
            case 'isNotEmpty':
                return fieldValue && fieldValue !== '';
            case 'between':
                const [min, max] = value;
                const num = parseFloat(fieldValue);
                return num >= min && num <= max;
            default:
                return true;
        }
    }

    /**
     * Evaluate visibility rules for a field
     * @param {object} fieldConfig - Field configuration
     * @param {object} formData - Current form data
     * @returns {boolean} Whether field should be visible
     */
    evaluateVisibility(fieldConfig, formData) {
        if (!fieldConfig.visibility) return true;

        const { conditions, logic = 'and' } = fieldConfig.visibility;
        if (!conditions || conditions.length === 0) return true;

        const results = conditions.map(cond => this.evaluateCondition(cond, formData));

        if (logic === 'and') {
            return results.every(r => r);
        } else {
            return results.some(r => r);
        }
    }

    /**
     * Get visible fields based on current form data
     * @param {object} formConfig - Form configuration
     * @param {object} formData - Current form data
     * @returns {object} Fields that should be visible
     */
    getVisibleFields(formConfig, formData) {
        const fields = formConfig.fields || {};
        const visibleFields = {};

        for (const [fieldId, fieldConfig] of Object.entries(fields)) {
            if (this.evaluateVisibility(fieldConfig, formData)) {
                visibleFields[fieldId] = fieldConfig;
            }
        }

        return visibleFields;
    }

    /**
     * Calculate field value based on formula
     * @param {string} formula - Formula expression
     * @param {object} formData - Current form data
     * @returns {*} Calculated value
     */
    calculateFormula(formula, formData) {
        if (!formula) return null;

        try {
            // Replace field references with values
            let expression = formula;
            
            // Match field references like {fieldId}
            const fieldRefs = formula.match(/\{(\w+)\}/g) || [];
            for (const ref of fieldRefs) {
                const fieldId = ref.slice(1, -1);
                const value = formData[fieldId] || 0;
                expression = expression.replace(ref, value);
            }

            // Handle built-in functions
            expression = this.replaceFormulaMethods(expression, formData);

            // Safe evaluation (avoid eval)
            return this.safeEvaluate(expression);
        } catch (error) {
            console.error('Formula calculation error:', error);
            return null;
        }
    }

    /**
     * Replace formula methods with actual calculations
     */
    replaceFormulaMethods(expression, formData) {
        // SUM function
        expression = expression.replace(/SUM\(([^)]+)\)/gi, (match, args) => {
            const fields = args.split(',').map(f => f.trim());
            const sum = fields.reduce((acc, field) => {
                const value = parseFloat(formData[field]) || 0;
                return acc + value;
            }, 0);
            return sum;
        });

        // DAYSBETWEEN function (returns difference in days, NOT including both start and end)
        // Use DAYSBETWEEN(start, end) + 1 in formula if you need to include both dates
        expression = expression.replace(/DAYSBETWEEN\(([^,]+),([^)]+)\)/gi, (match, start, end) => {
            const startField = start.trim();
            const endField = end.trim();
            const startDateStr = formData[startField];
            const endDateStr = formData[endField];
            
            if (!startDateStr || !endDateStr) return 0;
            
            // Parse dates as local dates to avoid timezone issues
            // Input format: "2024-01-15" or "2024/01/15" or ISO string
            const parseLocalDate = (dateStr) => {
                if (typeof dateStr === 'string') {
                    // Handle YYYY-MM-DD format
                    const match = dateStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2})/);
                    if (match) {
                        return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
                    }
                }
                return new Date(dateStr);
            };
            
            const startDate = parseLocalDate(startDateStr);
            const endDate = parseLocalDate(endDateStr);
            
            // Calculate difference in days
            const diffTime = endDate.getTime() - startDate.getTime();
            const diff = Math.round(diffTime / (1000 * 60 * 60 * 24));
            
            return isNaN(diff) ? 0 : diff;
        });

        // IF function
        expression = expression.replace(/IF\(([^,]+),([^,]+),([^)]+)\)/gi, (match, cond, trueVal, falseVal) => {
            // Simple condition evaluation
            const result = this.safeEvaluate(cond.trim());
            return result ? trueVal.trim() : falseVal.trim();
        });

        // ROUND function
        expression = expression.replace(/ROUND\(([^,]+),?(\d*)\)/gi, (match, value, decimals) => {
            const num = parseFloat(this.safeEvaluate(value.trim()));
            const dec = parseInt(decimals) || 0;
            return num.toFixed(dec);
        });

        return expression;
    }

    /**
     * Safe expression evaluation
     */
    safeEvaluate(expression) {
        // Remove any dangerous characters
        const sanitized = expression.replace(/[^0-9+\-*/().%<>=&|!?\s:,]/g, '');
        
        try {
            // Use Function constructor instead of eval
            return new Function(`return (${sanitized})`)();
        } catch {
            return 0;
        }
    }

    /**
     * Get default global schema (fallback)
     */
    getDefaultGlobalSchema() {
        return {
            fieldTypes: {
                enum: [
                    'text', 'textarea', 'number', 'currency', 'date', 
                    'datetime', 'time', 'select', 'multiselect', 'radio',
                    'checkbox', 'file', 'email', 'phone', 'hidden', 'calculated'
                ],
                descriptions: {
                    text: 'Single line text input',
                    textarea: 'Multi-line text input',
                    number: 'Numeric input',
                    currency: 'Currency input',
                    date: 'Date picker',
                    datetime: 'Date and time picker',
                    time: 'Time picker',
                    select: 'Dropdown selection',
                    multiselect: 'Multiple selection',
                    radio: 'Radio button group',
                    checkbox: 'Checkbox',
                    file: 'File upload',
                    email: 'Email input',
                    phone: 'Phone input',
                    hidden: 'Hidden field',
                    calculated: 'Calculated field'
                }
            },
            stepTypes: {
                enum: ['submit', 'approval', 'notification', 'condition', 'completed'],
                descriptions: {
                    submit: 'Form submission',
                    approval: 'Approval step',
                    notification: 'Notification step',
                    condition: 'Conditional branching',
                    completed: 'Workflow completion'
                }
            },
            roleTypes: {
                enum: [
                    'applicant', 'direct_manager', 'manager_chain', 
                    'department_head', 'role', 'group', 'specific_user',
                    'finance', 'hr', 'admin'
                ],
                descriptions: {
                    applicant: 'Form submitter',
                    direct_manager: 'Direct manager',
                    manager_chain: 'Manager hierarchy',
                    department_head: 'Department head',
                    role: 'Specific role',
                    group: 'User group',
                    specific_user: 'Named user',
                    finance: 'Finance department',
                    hr: 'HR department',
                    admin: 'Administrator'
                }
            },
            actionTypes: {
                enum: ['submit', 'save', 'approve', 'reject', 'return', 'delegate', 'comment'],
                descriptions: {
                    submit: 'Submit form',
                    save: 'Save draft',
                    approve: 'Approve',
                    reject: 'Reject',
                    return: 'Return for revision',
                    delegate: 'Delegate',
                    comment: 'Add comment'
                }
            },
            currencies: {
                enum: ['USD', 'EUR', 'CNY', 'TWD', 'HKD', 'JPY'],
                symbols: {
                    USD: '$',
                    EUR: '€',
                    CNY: '¥',
                    TWD: 'NT$',
                    HKD: 'HK$',
                    JPY: '¥'
                }
            }
        };
    }

    /**
     * Clear all caches
     */
    clearCache() {
        schemaCache.clear();
        configCache.clear();
        this.globalSchema = null;
    }
}

// Export singleton instance
export const schemaService = new SchemaService();

// Export class for testing
export { SchemaService };

export default schemaService;
